// src/app/models/sheet-types.ts
//
// Canonical sheet identifiers shared by the filing-form container and the
// SheetApplicabilityService. These were historically declared inline in
// `filing-form-container.component.ts`; they were promoted here so the
// applicability logic could be extracted into a DI-free service without a
// circular dependency on the component.

import type { FilingFormState } from '../services/mbrs/filing-form-state.service';
import { SHEET_KEYS } from './sheet-keys';

/** The kebab-case identifier for every sheet the container can render. */
export type SheetType =
  | 'filing-prerequisites'
  | 'filing-info'
  | 'scope-of-filing'
  | 'stock-exchange'
  | 'directors-report'
  | 'statement-by-directors'
  | 'director-business-review'
  | 'auditors-report'
  | 'statement-of-financial-position'
  | 'sub-classification'
  | 'statement-of-financial-position-liquidity'
  | 'sub-classification-liquidity'
  | 'statement-of-profit-or-loss'
  | 'analysis-of-profit-or-loss'
  | 'statement-of-profit-or-loss-by-nature'
  | 'analysis-of-profit-or-loss-by-nature'
  | 'statement-of-comprehensive-income'
  | 'statement-of-comprehensive-income-before-tax'
  | 'statement-of-cash-flows-direct'
  | 'statement-of-cash-flows-indirect'
  | 'statement-of-changes-in-equity'
  | 'notes-corporate-information'
  | 'notes-accounting-policies'
  | 'notes-list-of-notes'
  | 'notes-issued-capital'
  | 'notes-related-party-transactions';

/**
 * Human-readable name for every sheet, as shown in the selector dropdown.
 *
 * The filing-form container's `sheets: SheetOption[]` array reads its `label`
 * from here rather than carrying its own literals, so there is exactly one copy
 * of each name. Every other feature that has to *name* a sheet in copy reads it
 * here too:
 *
 *  - the field-link tooltips (`field-link-label.util.ts`, "Open … in
 *    Sub-classification of Assets, Liabilities & Equity");
 *  - each sheet's own card header, via
 *    `FilingFormStateService.getSheetTitleWithSubmissionType()`;
 *  - the submit-time "this sheet was reverted" warnings, via
 *    `SHEET_LABELS_BY_STATE_KEY` below.
 *
 * Those last two used to keep their own literals and had already drifted from
 * this map and from each other — an apostrophe in "Auditor's Report", a
 * trailing "Disclosure" on Stock Exchange, "(Direct)" against "(Direct
 * Method)". Rename a sheet HERE and every surface follows.
 *
 * Note this map is a lookup, NOT an ordering: the selector sequence lives in the
 * container's array literal and deliberately follows the SSM MBRS Prep Tool's
 * order, not role-code order. Reordering the entries below changes nothing.
 *
 * `filing-prerequisites` and `filing-info` share the name "Filing Information"
 * on purpose — the container's phase-aware filter surfaces exactly one of them
 * at a time, so the two can never collide on screen.
 */
export const SHEET_DISPLAY_NAMES: Readonly<Record<SheetType, string>> = {
  'filing-prerequisites': 'Filing Information',
  'filing-info': 'Filing Information',
  'scope-of-filing': 'Scope of Filing',
  'stock-exchange': 'Stock Exchange',
  'directors-report': "Director's Report",
  'statement-by-directors': 'Statement by Directors',
  'director-business-review': 'Director Business Review',
  'auditors-report': "Auditor's Report",
  'statement-of-financial-position': 'Statement of Financial Position',
  'sub-classification': 'Sub-classification of Assets, Liabilities & Equity',
  'statement-of-financial-position-liquidity':
    'Statement of Financial Position (Liquidity)',
  'sub-classification-liquidity': 'Sub-classification (Liquidity Order)',
  'statement-of-profit-or-loss': 'Statement of Profit or Loss',
  'analysis-of-profit-or-loss': 'Analysis of Profit or Loss',
  'statement-of-profit-or-loss-by-nature':
    'Statement of Profit or Loss (By Nature)',
  'analysis-of-profit-or-loss-by-nature':
    'Analysis of Profit or Loss (By Nature)',
  'statement-of-comprehensive-income': 'Statement of Comprehensive Income',
  'statement-of-comprehensive-income-before-tax':
    'Statement of Comprehensive Income (Before Tax)',
  'statement-of-cash-flows-direct': 'Statement of Cash Flows (Direct)',
  'statement-of-cash-flows-indirect': 'Statement of Cash Flows (Indirect)',
  'statement-of-changes-in-equity': 'Statement of Changes in Equity',
  'notes-corporate-information': 'Notes - Corporate Information',
  'notes-accounting-policies': 'Notes - Summary of Material Accounting Policies',
  'notes-list-of-notes': 'Notes - List of Notes',
  'notes-issued-capital': 'Notes - Issued Capital',
  'notes-related-party-transactions': 'Notes - Related Party Transactions',
};

/**
 * Each sheet's role label as it appears in its own load-failure codes
 * (`MBRS-S<label>-…`), i.e. the first argument its component passes to
 * `SheetLoadTraceService.beginSheetLoad`. Used to name the sheet a user was
 * LEAVING when the pre-navigation session gate catches a dead session.
 * `npm run check:sheet-load-trace` fails if any component's label drifts.
 *
 * Two labels are not a bare role number: both role-15 sheets share role 15, so
 * each carries its taxonomy code to stay distinguishable.
 */
export const SHEET_DIAGNOSTIC_ROLE_LABELS: Readonly<Record<SheetType, string>> = {
  'filing-prerequisites': '0',
  'filing-info': '1',
  'scope-of-filing': '2',
  'stock-exchange': '3',
  'directors-report': '4',
  'statement-by-directors': '5',
  'director-business-review': '6',
  'auditors-report': '7',
  'statement-of-financial-position': '8',
  'sub-classification': '9',
  'statement-of-financial-position-liquidity': '10',
  'sub-classification-liquidity': '11',
  'statement-of-profit-or-loss': '12',
  'analysis-of-profit-or-loss': '13',
  'statement-of-profit-or-loss-by-nature': '14',
  'analysis-of-profit-or-loss-by-nature': '15-320100',
  'statement-of-comprehensive-income': '15-410000',
  'statement-of-comprehensive-income-before-tax': '16',
  'statement-of-cash-flows-direct': '17',
  'statement-of-cash-flows-indirect': '18',
  'statement-of-changes-in-equity': '19',
  'notes-corporate-information': '20',
  'notes-accounting-policies': '21',
  'notes-list-of-notes': '22',
  'notes-issued-capital': '23',
  'notes-related-party-transactions': '24',
};

/**
 * `FilingFormState` slice key -> the `SheetType` that names the same sheet.
 *
 * Needed because sheets are addressed by two different key namespaces: state
 * slices use camelCase keys (`SHEET_KEYS`), the router/selector uses kebab
 * `SheetType`. Keeping the pairing in ONE table is what lets every consumer
 * read its name from `SHEET_DISPLAY_NAMES` no matter which key it holds.
 *
 * `filingPrerequisites` and `filingInfo` both point at the shared "Filing
 * Information" name, exactly as `SHEET_DISPLAY_NAMES` already does.
 */
export const STATE_KEY_TO_SHEET_TYPE: Readonly<
  Record<keyof FilingFormState, SheetType>
> = {
  filingPrerequisites: 'filing-prerequisites',
  filingInfo: 'filing-info',
  scopeOfFiling: 'scope-of-filing',
  stockExchangeDisclosure: 'stock-exchange',
  directorsReport: 'directors-report',
  statementByDirectors: 'statement-by-directors',
  directorBusinessReview: 'director-business-review',
  auditorsReport: 'auditors-report',
  statementOfFinancialPosition: 'statement-of-financial-position',
  subClassificationOfAssetsLiabilitiesEquity: 'sub-classification',
  statementOfFinancialPositionLiquidity:
    'statement-of-financial-position-liquidity',
  subClassificationLiquidity: 'sub-classification-liquidity',
  statementOfProfitOrLoss: 'statement-of-profit-or-loss',
  analysisOfProfitOrLoss: 'analysis-of-profit-or-loss',
  statementOfProfitOrLossByNature: 'statement-of-profit-or-loss-by-nature',
  analysisOfProfitOrLossByNature: 'analysis-of-profit-or-loss-by-nature',
  statementOfComprehensiveIncome: 'statement-of-comprehensive-income',
  statementOfComprehensiveIncomeBeforeTax:
    'statement-of-comprehensive-income-before-tax',
  statementOfCashFlowsDirectMethod: 'statement-of-cash-flows-direct',
  statementOfCashFlowsIndirectMethod: 'statement-of-cash-flows-indirect',
  statementOfChangesInEquity: 'statement-of-changes-in-equity',
  notesCorporateInformation: 'notes-corporate-information',
  notesAccountingPolicies: 'notes-accounting-policies',
  notesListOfNotes: 'notes-list-of-notes',
  notesIssuedCapital: 'notes-issued-capital',
  notesRelatedPartyTransactions: 'notes-related-party-transactions',
};

/**
 * The same human-readable names as {@link SHEET_DISPLAY_NAMES}, keyed by
 * `FilingFormState` slice key.
 *
 * DERIVED, never hand-written — this is what keeps the sheet selector, the
 * field-link tooltips and each sheet's card header from drifting apart.
 * Three hand-maintained copies
 * had already diverged before this was introduced (an apostrophe in
 * "Auditor's Report", "(Direct)" vs "(Direct Method)", a trailing
 * "Disclosure"). Add a name in `SHEET_DISPLAY_NAMES` only.
 */
export const SHEET_LABELS_BY_STATE_KEY: Readonly<
  Record<keyof FilingFormState, string>
> = Object.freeze(
  Object.fromEntries(
    (
      Object.entries(STATE_KEY_TO_SHEET_TYPE) as Array<
        [keyof FilingFormState, SheetType]
      >
    ).map(([stateKey, sheetType]) => [stateKey, SHEET_DISPLAY_NAMES[sheetType]])
  ) as Record<keyof FilingFormState, string>
);

/** A selectable sheet option as rendered in the sheet selector dropdown. */
export interface SheetOption {
  label: string;
  value: SheetType;
  icon: string;
  description: string;
}

/**
 * Canonical `FilingFormState` key → `SheetType` (kebab identifier) bridge.
 *
 * The container addresses sheets by `SheetType` (the dropdown / applicability
 * vocabulary) while state, validators, and the headless orchestrator address
 * them by `keyof FilingFormState`. This map is the single crossing point — it
 * lets the headless `ConversionValidatorService` translate its validated
 * `FilingFormState` key set into the `SheetOption[]` that
 * `SheetApplicabilityService.computeEnabledSheets` consumes (and back), so the
 * listing/dashboard validate exactly the sheets the editor shows. Covers every
 * `SHEET_KEYS` entry, including the alternative-layout variants that have no
 * `retrieveSlug` in `sheet-descriptors.ts`.
 */
export const SHEET_TYPE_BY_STATE_KEY: Readonly<
  Record<keyof FilingFormState, SheetType>
> = {
  [SHEET_KEYS.filingPrerequisites]: 'filing-prerequisites',
  [SHEET_KEYS.filingInfo]: 'filing-info',
  [SHEET_KEYS.scopeOfFiling]: 'scope-of-filing',
  [SHEET_KEYS.stockExchangeDisclosure]: 'stock-exchange',
  [SHEET_KEYS.directorsReport]: 'directors-report',
  [SHEET_KEYS.statementByDirectors]: 'statement-by-directors',
  [SHEET_KEYS.directorBusinessReview]: 'director-business-review',
  [SHEET_KEYS.auditorsReport]: 'auditors-report',
  [SHEET_KEYS.statementOfFinancialPosition]: 'statement-of-financial-position',
  [SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity]: 'sub-classification',
  [SHEET_KEYS.statementOfFinancialPositionLiquidity]:
    'statement-of-financial-position-liquidity',
  [SHEET_KEYS.subClassificationLiquidity]: 'sub-classification-liquidity',
  [SHEET_KEYS.statementOfProfitOrLoss]: 'statement-of-profit-or-loss',
  [SHEET_KEYS.analysisOfProfitOrLoss]: 'analysis-of-profit-or-loss',
  [SHEET_KEYS.statementOfProfitOrLossByNature]:
    'statement-of-profit-or-loss-by-nature',
  [SHEET_KEYS.analysisOfProfitOrLossByNature]:
    'analysis-of-profit-or-loss-by-nature',
  [SHEET_KEYS.statementOfComprehensiveIncome]: 'statement-of-comprehensive-income',
  [SHEET_KEYS.statementOfComprehensiveIncomeBeforeTax]:
    'statement-of-comprehensive-income-before-tax',
  [SHEET_KEYS.statementOfCashFlowsDirectMethod]: 'statement-of-cash-flows-direct',
  [SHEET_KEYS.statementOfCashFlowsIndirectMethod]:
    'statement-of-cash-flows-indirect',
  [SHEET_KEYS.statementOfChangesInEquity]: 'statement-of-changes-in-equity',
  [SHEET_KEYS.notesCorporateInformation]: 'notes-corporate-information',
  [SHEET_KEYS.notesAccountingPolicies]: 'notes-accounting-policies',
  [SHEET_KEYS.notesListOfNotes]: 'notes-list-of-notes',
  [SHEET_KEYS.notesIssuedCapital]: 'notes-issued-capital',
  [SHEET_KEYS.notesRelatedPartyTransactions]: 'notes-related-party-transactions',
};
